import { useEffect, useRef, useState } from "react";

type HlsConstructor = (typeof import("hls.js"))["default"];

interface UseLazyVideoPlayerOptions {
  videoSrc: string;
  enabled?: boolean;
}

interface UseLazyVideoPlayerReturn {
  HlsClass: HlsConstructor | null;
  isLoading: boolean;
  isHlsNeeded: boolean;
}

/**
 * Custom hook to lazy load HLS.js only when needed
 * Defers loading until after initial render to improve performance
 */
export function useLazyVideoPlayer({
  videoSrc,
  enabled = true,
}: UseLazyVideoPlayerOptions): UseLazyVideoPlayerReturn {
  const [HlsClass, setHlsClass] = useState<HlsConstructor | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedRef = useRef(false);

  // Check if the video source requires HLS
  const isHlsNeeded = /\.m3u8($|\?)/.test(videoSrc);

  useEffect(() => {
    // Only load HLS.js if:
    // 1. It's needed (m3u8 file)
    // 2. It hasn't been loaded yet
    // 3. The hook is enabled
    if (!isHlsNeeded || hasLoadedRef.current || !enabled) {
      return;
    }

    setIsLoading(true);

    // Defer loading using requestIdleCallback when available for better performance
    const loadHls = () => {
      import("hls.js")
        .then(({ default: Hls }) => {
          // Optional safety: only set if supported
          if (Hls.isSupported()) {
            setHlsClass(() => Hls);
            hasLoadedRef.current = true;
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Failed to load HLS.js:", error);
          setIsLoading(false);
          hasLoadedRef.current = false;
        });
    };

    let cleanupHandle: ReturnType<typeof setTimeout> | number | undefined;
    let isIdleCallback = false;

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      cleanupHandle = window.requestIdleCallback(loadHls, { timeout: 2000 });
      isIdleCallback = true;
    } else {
      cleanupHandle = setTimeout(loadHls, 0);
    }

    return () => {
      if (
        isIdleCallback &&
        typeof window !== "undefined" &&
        "cancelIdleCallback" in window &&
        typeof cleanupHandle === "number"
      ) {
        window.cancelIdleCallback(cleanupHandle);
      } else if (cleanupHandle !== undefined) {
        clearTimeout(cleanupHandle);
      }
    };
  }, [videoSrc, isHlsNeeded, enabled]);

  return {
    HlsClass,
    isLoading,
    isHlsNeeded,
  };
}
